import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button as MuiButton,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import FormContainer from '../../../common/FormContainer/FormContainer';
import Button from '../../../common/Button/Button';
import { setIdeaAnswers } from '../../../store/slices/ideaSubmissionSlice';

const steps = [
  'Problem Statement',
  'Understanding the Problem',
  'Proposed Solution'
];

// const MIN_CHARS = 30;

/* ---------------- Textarea size helper ---------------- */
const getTextareaRows = (maxChars = 200, isMobile) => {
  if (maxChars <= 200) return isMobile ? 3 : 4;
  if (maxChars <= 400) return isMobile ? 4 : 6;
  return isMobile ? 5 : 8;
};

export function IdeaQuestionsStep({ onNext, onBack, questions }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const ideaAnswers = useSelector(
    (state) => state.ideaSubmission.ideaAnswers
  );

  const [activeStep, setActiveStep] = useState(0);
  const [localAnswers, setLocalAnswers] = useState(ideaAnswers);
  const [isValid, setIsValid] = useState(false);

  /* ---------------- Helpers (STABLE) ---------------- */
  const getQuestion = useCallback(
    (key) => questions.find((q) => q.key === key),
    [questions]
  );

  const currentQuestions = useCallback(() => {
    if (activeStep === 0) return ['problem'];
    if (activeStep === 1) return ['affectedWho', 'why'];
    if (activeStep === 2) return ['solution'];
    return [];
  }, [activeStep]);

  /* ---------------- Validation ---------------- */
  useEffect(() => {
  const keys = currentQuestions();

  const valid = keys.every((key) => {
    const q = getQuestion(key);
    const value = (localAnswers[key] || '').trim();

    const minChars = q?.minChars ?? 0;

    if (q?.required && value.length < minChars) return false;
    if (q?.maxChars && value.length > q.maxChars) return false;

    return true;
  });

  setIsValid(valid);
}, [localAnswers, currentQuestions, getQuestion]);


  /* ---------------- Sync from Redux ---------------- */
  useEffect(() => {
    setLocalAnswers(ideaAnswers);
  }, [ideaAnswers]);

  /* ---------------- Handlers ---------------- */
  const handleChange = (key, value, maxChars) => {
    if (maxChars && value.length > maxChars) return;

    setLocalAnswers((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNextStep = () => {
    dispatch(setIdeaAnswers(localAnswers));

    if (activeStep === steps.length - 1) {
      onNext();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBackStep = () => {
    if (activeStep === 0) {
      onBack();
    } else {
      setActiveStep((s) => s - 1);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <FormContainer>
      {/* Stepper */}
      <Stepper
        activeStep={activeStep}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        alternativeLabel={!isMobile}
        sx={{ mb: 4 }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                  textAlign: 'center',
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Questions */}
      {currentQuestions().map((key) => {
        const q = getQuestion(key);
        const value = localAnswers[key] || '';
        const trimmedLength = value.trim().length;
const minChars = q?.minChars ?? 0;
const showMinError = trimmedLength > 0 && trimmedLength < minChars;

        return (
          <Box key={key} sx={{ mb: 4 }}>
            {/* Label + Info */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                {q.label}
                {q.required && (
                  <Typography component="span" color="error">
                    {' '}*
                  </Typography>
                )}
              </Typography>

              {q.helpText && (
                <Tooltip title={q.helpText} arrow placement="top">
                  <IconButton size="small">
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#64748b' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Textarea */}
            <TextField
              fullWidth
              multiline
              rows={getTextareaRows(q.maxChars, isMobile)}
              value={value}
              error={showMinError}
              onChange={(e) =>
                handleChange(key, e.target.value, q.maxChars)
              }
              sx={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '14px',
                boxShadow: isMobile
                  ? '0 6px 14px rgba(30, 60, 120, 0.12)'
                  : '0 10px 25px rgba(30, 60, 120, 0.12)',
              }}
            />

            {/* Helper text */}
            <Typography
              variant="caption"
              color={showMinError ? 'error' : 'text.secondary'}
              mt={0.5}
              display="block"
            >
Minimum {q.minChars} characters required · {value.length}/{q.maxChars}
            </Typography>
          </Box>
        );
      })}

      {/* Actions */}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={2}
        justifyContent="space-between"
      >
        <MuiButton onClick={handleBackStep} fullWidth={isMobile}>
          Back
        </MuiButton>

        <Button
          onClick={handleNextStep}
          disabled={!isValid}
          fullWidth={isMobile}
        >
          Next
        </Button>
      </Box>
    </FormContainer>
  );
}
